// ============================================================================
// SkillCardModal - wraps SkillCard in a dialog with the privacy toggle
// ============================================================================
// Used from ProfilePage so the user can preview their own card and flip the
// publicSkillCard switch in one place.
// ============================================================================
import React, { useEffect, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalBody, ModalCloseButton,
  Box, HStack, Text, Switch, FormControl, FormLabel, useToast,
} from '@chakra-ui/react';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import gameApiService from '../../services/gameApiService';
import SkillCard from './SkillCard';

interface SkillCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId?: string; // undefined = self; non-self hides the privacy toggle
}

const SkillCardModal: React.FC<SkillCardModalProps> = ({ isOpen, onClose, userId }) => {
  const [isPublic, setIsPublic] = useState(false);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const isSelf = !userId || userId === auth.currentUser?.uid;

  useEffect(() => {
    if (!isOpen || !isSelf) return;
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', uid));
        setIsPublic(snap.exists() && snap.data().publicSkillCard === true);
      } catch {
        /* ignore */
      }
    })();
  }, [isOpen, isSelf]);

  const handleTogglePublic = async (next: boolean) => {
    setIsPublic(next);
    setSaving(true);
    const r = await gameApiService.setSkillCardPrivacy(next);
    setSaving(false);
    if (!r.success) {
      // revert on failure
      setIsPublic(!next);
      toast({
        status: 'error',
        title: 'Could not update privacy',
        description: r.error || 'Please try again',
      });
    } else {
      toast({
        status: 'success',
        title: next ? 'Player rating is now public' : 'Player rating is now private',
        duration: 2200,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay bg="rgba(0,0,0,0.7)" />
      <ModalContent bg="transparent" boxShadow="none" maxW="520px">
        <ModalCloseButton color="white" zIndex={2} />
        <ModalBody p={0}>
          <SkillCard userId={userId} />
          {isSelf && (
            <Box mt={4} bg="gray.800" border="1px solid" borderColor="gray.700"
              borderRadius="md" p={4}>
              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <FormLabel htmlFor="public-skill-card" mb={0} color="white" fontSize="sm">
                    Make player rating public
                  </FormLabel>
                  <Text fontSize="xs" color="gray.400">
                    When on, other players can search and view your rating card.
                  </Text>
                </Box>
                <Switch
                  id="public-skill-card"
                  colorScheme="cyan"
                  isChecked={isPublic}
                  isDisabled={saving}
                  onChange={e => handleTogglePublic(e.target.checked)}
                />
              </FormControl>
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default SkillCardModal;
